import type { Account, AccountClass, AccountType, AccountNode, Contact, Entry } from '@taler/api-client'
import {
  type CurrencyCode,
  type Locale,
  type LocalDate,
  requireCurrencyCode,
  resolveLocalized,
} from '@taler/domain'

/**
 * One namespace over accounts and contacts: a row's target is always an accountId, and
 * whether the user reached it through a category or a contact is presentation only (see
 * `Contact.accountId` and AGENTS.md's captain note on the batch form).
 */
export interface Target {
  readonly id: string
  readonly source: 'account' | 'contact'
  readonly leaf: string
  readonly path: string
  /** Undefined means the target accepts a posting in any currency. */
  readonly currency: CurrencyCode | undefined
  /** Account codes from the class root down to this account; empty for a contact. */
  readonly codePath: readonly string[]
  readonly accountClass?: AccountClass
  readonly accountType?: AccountType
  readonly icon?: string
  readonly color?: string
  /** Enclosing accounts, class root excluded, outermost first; drives the picker's group headers. */
  readonly ancestors?: readonly TargetAncestor[]
  /** Set when this target belongs to another ledger than the one the picker is otherwise scoped to. */
  readonly ledgerId?: string
}

export interface TargetAncestor {
  readonly id: string
  readonly name: string
  readonly icon: string
  readonly color: string
}

function accountName(account: Account, locale: Locale, contactNames: ReadonlyMap<string, string>): string {
  const named = resolveLocalized(account.names, locale)?.name
  if (named !== undefined) return named
  const contactName = contactNames.get(account.id)
  if (contactName !== undefined) return contactName
  const lastSegment = account.code.split('.').pop() ?? account.code
  return lastSegment.charAt(0) + lastSegment.slice(1).toLowerCase().replaceAll('_', ' ')
}

/** Only ASSET and LIABILITY accounts have one home currency a posting must match
 * (AGENTS.md: the real backend gives INCOME/EXPENSE/EQUITY no home currency at all and
 * accepts a posting in any currency - the fixture's own currency on those classes is
 * cosmetic, not a wire constraint). */
const CLASSES_WITH_HOME_CURRENCY: readonly AccountClass[] = ['ASSET', 'LIABILITY']

/** Every non-archived, postable leaf whose class is one of `classes` - ancestry joins into
 * `path` the same way the transactions list builds a tooltip breadcrumb. A system account
 * (`EQUITY.OPENING`, `.FX_GAIN_LOSS`) is plumbing, left out unless `offersSystem` says a module
 * owns what posting into it means (the seeded `EXPENSE.CAR` leaves are system accounts too). */
export function accountTargets(
  nodes: readonly AccountNode[],
  classes: readonly AccountClass[],
  locale: Locale,
  offersSystem: (target: Target) => boolean = () => false,
  contactNames: ReadonlyMap<string, string> = new Map(),
): Target[] {
  const out: Target[] = []
  const walk = (
    level: readonly AccountNode[],
    ancestors: readonly string[],
    ancestorCodes: readonly string[],
    ancestorRefs: readonly TargetAncestor[],
  ): void => {
    for (const node of level) {
      const { account, children } = node
      if (account.archivedAt !== undefined) continue
      const label = accountName(account, locale, contactNames)
      const codePath = [...ancestorCodes, account.code]
      // A contact's backing account is offered as the contact itself, never as an account.
      if (account.isPostable && classes.includes(account.accountClass) && !contactNames.has(account.id)) {
        const hasHomeCurrency = CLASSES_WITH_HOME_CURRENCY.includes(account.accountClass)
        const target: Target = {
          id: account.id,
          source: 'account',
          leaf: label,
          path: [...ancestors, label].join(' / '),
          currency:
            hasHomeCurrency && account.currency !== undefined
              ? requireCurrencyCode(account.currency)
              : undefined,
          codePath,
          accountClass: account.accountClass,
          accountType: account.type,
          icon: account.icon,
          color: account.color,
          ancestors: ancestorRefs.slice(1),
        }
        if (!account.isSystem || offersSystem(target)) out.push(target)
      }
      walk(children, [...ancestors, label], codePath, [
        ...ancestorRefs,
        { id: account.id, name: label, icon: account.icon, color: account.color },
      ])
    }
  }
  walk(nodes, [], [], [])
  return out
}

/** Another ledger's accounts, offered under a synthetic ledger group so the picker renders one
 * header per ledger alongside the active ledger's own groups (AccountPicker's includeOtherLedgers). */
export function otherLedgerTargets(
  ledger: { readonly id: string; readonly name: string },
  nodes: readonly AccountNode[],
  classes: readonly AccountClass[],
  locale: Locale,
): Target[] {
  const ledgerAncestor: TargetAncestor = {
    id: `ledger:${ledger.id}`,
    name: ledger.name,
    icon: 'GLOBAL',
    color: 'var(--color-text-muted)',
  }
  return accountTargets(nodes, classes, locale).map((target) => ({
    ...target,
    ledgerId: ledger.id,
    path: `${ledger.name} / ${target.path}`,
    ancestors: [ledgerAncestor, ...(target.ancestors ?? [])],
  }))
}

/** One target per contact - its one backing account is currency-agnostic (it can hold a
 * genuine balance in any currency at once), so the target accepts a posting in any currency
 * rather than pinning to one the way an account leaf's own currency does. */
export function contactTargets(contacts: readonly Contact[]): Target[] {
  return contacts.map((contact) => ({
    id: contact.accountId,
    source: 'contact' as const,
    leaf: contact.name,
    path: contact.name,
    currency: undefined,
    codePath: [],
  }))
}

export interface Frecency {
  readonly count: number
  readonly lastUsedDaysAgo: number
}

/**
 * Counted from the ledger's own recent journal, not a server aggregate: "31x - yesterday"
 * is a presentation ranking, not a figure this screen displays as fact, so summing it from
 * whatever page of history is already in hand is the client-side arithmetic AGENTS.md's
 * currency rule (which this is not) leaves untouched.
 */
export function computeFrecency(entries: readonly Entry[], today: LocalDate): ReadonlyMap<string, Frecency> {
  const out = new Map<string, Frecency>()
  for (const entry of entries) {
    const daysAgo = today.epochDay - entry.date.epochDay
    for (const posting of entry.postings) {
      const existing = out.get(posting.accountId)
      out.set(posting.accountId, {
        count: (existing?.count ?? 0) + 1,
        lastUsedDaysAgo: existing === undefined ? daysAgo : Math.min(existing.lastUsedDaysAgo, daysAgo),
      })
    }
  }
  return out
}

export interface RankedTarget {
  readonly target: Target
  readonly frecency: Frecency | undefined
}

/** Most-used-first, then alphabetical - the ordering `AccountCombobox` renders directly,
 * split at the point frecency data runs out ("everything else that matches"). */
export function rankTargets(
  targets: readonly Target[],
  frecency: ReadonlyMap<string, Frecency>,
): RankedTarget[] {
  return [...targets]
    .map((target) => ({ target, frecency: frecency.get(target.id) }))
    .sort((a, b) => {
      const byCount = (b.frecency?.count ?? 0) - (a.frecency?.count ?? 0)
      if (byCount !== 0) return byCount
      const byRecency = (a.frecency?.lastUsedDaysAgo ?? Infinity) - (b.frecency?.lastUsedDaysAgo ?? Infinity)
      if (byRecency !== 0) return byRecency
      return a.target.leaf.localeCompare(b.target.leaf)
    })
}

export function matchesQuery(target: Target, query: string): boolean {
  if (query.trim() === '') return true
  const needle = query.trim().toLowerCase()
  return target.leaf.toLowerCase().includes(needle) || target.path.toLowerCase().includes(needle)
}

export type FrecencyWhen = 'today' | 'yesterday' | 'daysAgo' | undefined

/** Yesterday reads better than "1 day ago" for the frecency hint the mockup shows next
 * to a match; anything older than a week just gives its count. The translation itself is
 * the caller's job - this module stays free of i18n key strings and their odd `t` typing. */
export function frecencyWhen(frecency: Frecency): FrecencyWhen {
  if (frecency.lastUsedDaysAgo <= 0) return 'today'
  if (frecency.lastUsedDaysAgo === 1) return 'yesterday'
  if (frecency.lastUsedDaysAgo <= 7) return 'daysAgo'
  return undefined
}
